// ===========================================
// Idea Tab Component - Big Rocks Framework
// ===========================================
// Four cornerstone questions for brainstorming any app idea
// Focused on the essentials: Vision, User, Problem, Experience

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Lightbulb,      // The Vision
  User,           // The User
  Target,         // The Problem
  Sparkles,       // The Experience
  Save,
  CheckCircle,
  FileText,       // README.md button icon
  Loader          // Loading spinner for AI generation
} from 'lucide-react';
import { projectAPI, aiAPI } from '../../services/api';
import { useProjects } from '../../context/ProjectContext';
import './IdeaTab.scss';

// ===========================================
// Section Configuration - Big Rocks Framework
// ===========================================
// Four cornerstone questions for brainstorming any app idea
const SECTIONS = [
  {
    id: 'vision',
    label: 'The Vision',
    icon: Lightbulb,
    placeholder: 'What are you building?\n\nDescribe the app in a few sentences. What does it do? What\'s the big idea?\n\nDon\'t worry about being perfect — just get the concept down.',
    color: '#f59e0b'  // amber
  },
  {
    id: 'user',
    label: 'The User',
    icon: User,
    placeholder: 'Who is this for?\n\nBe specific. Not "everyone" — who\'s the person that will love this? What\'s their life like? What do they care about?',
    color: '#8b5cf6'  // purple
  },
  {
    id: 'problem',
    label: 'The Problem',
    icon: Target,
    placeholder: 'Why will they care?\n\nWhat pain, frustration, or need does this solve? How are they dealing with it today? Why isn\'t that good enough?',
    color: '#ef4444'  // red
  },
  {
    id: 'experience',
    label: 'The Experience',
    icon: Sparkles,
    placeholder: 'What does using it feel like?\n\nImagine someone using your app. What do they do? How does it feel? What\'s the moment they go "this is great"?',
    color: '#10b981'  // green
  }
];

// ===========================================
// Debounce Hook - Delays updates for auto-save
// ===========================================
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    // Set up timer to update debounced value after delay
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Clean up timer on value change or unmount
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

// ===========================================
// Main IdeaTab Component
// ===========================================
const IdeaTab = ({ project }) => {
  const { setCurrentProject } = useProjects();
  const projectId = project._id || project.id;

  // ===========================================
  // State Management
  // ===========================================
  // Initialize form data from project's ideaWorkshop or empty strings
  const [formData, setFormData] = useState(() => ({
    vision: project.ideaWorkshop?.vision || '',
    user: project.ideaWorkshop?.user || '',
    problem: project.ideaWorkshop?.problem || '',
    experience: project.ideaWorkshop?.experience || ''
  }));

  // Track save status for UI feedback
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  
  // Track which section is loading from README AI generation
  const [loadingSection, setLoadingSection] = useState(null);

  // Debounce form data for auto-save (1.5 second delay)
  const debouncedFormData = useDebounce(formData, 1500);

  // ===========================================
  // Auto-save Effect - Saves when debounced data changes
  // ===========================================
  useEffect(() => {
    // Check if any field has changed from the project's current state
    const hasChanges = SECTIONS.some(
      section => debouncedFormData[section.id] !== (project.ideaWorkshop?.[section.id] || '')
    );

    // Skip if no changes (prevents initial render save)
    if (!hasChanges) return;

    // Save to backend
    const saveData = async () => {
      setSaving(true);
      setError(null);

      try {
        // Update project with new ideaWorkshop data
        const response = await projectAPI.update(projectId, {
          ideaWorkshop: debouncedFormData
        });

        // Update local project state with response
        if (response.data?.data?.project) {
          setCurrentProject(response.data.data.project);
        }

        // Show saved indicator briefly
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      } catch (err) {
        console.error('Failed to save idea workshop:', err);
        setError('Failed to save. Please try again.');
      } finally {
        setSaving(false);
      }
    };

    saveData();
  }, [debouncedFormData, projectId, project.ideaWorkshop, setCurrentProject]);

  // ===========================================
  // Handle Input Change - Updates form state
  // ===========================================
  const handleChange = useCallback((sectionId, value) => {
    setFormData(prev => ({
      ...prev,
      [sectionId]: value
    }));
    // Clear saved indicator when user types
    setSaved(false);
  }, []);

  // ===========================================
  // Generate Section from README - AI-powered content fill
  // ===========================================
  // Uses xAI to analyze README.md and generate relevant content for the section
  const handleGenerateFromReadme = async (sectionId) => {
    // Check if project has README content uploaded
    if (!project.readmeContent) {
      setError('No README.md uploaded. Upload one on the Overview tab first.');
      setTimeout(() => setError(null), 4000);
      return;
    }

    setLoadingSection(sectionId);
    setError(null);

    try {
      // Call AI endpoint to generate section content from README
      const response = await aiAPI.readmeToIdea(projectId, sectionId);
      
      if (response.data.success) {
        // Update form data with AI-generated content
        const newContent = response.data.data.content;
        const updatedFormData = {
          ...formData,
          [sectionId]: newContent
        };
        setFormData(updatedFormData);
        
        // Save immediately to backend (bypass debounce for AI-generated content)
        const updateResponse = await projectAPI.update(projectId, {
          ideaWorkshop: updatedFormData
        });
        
        // Update local project state with response
        if (updateResponse.data?.data?.project) {
          setCurrentProject(updateResponse.data.data.project);
        }
        
        // Show saved indicator
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      }
    } catch (err) {
      console.error('Failed to generate from README:', err);
      setError(err.response?.data?.message || 'Failed to generate content. Please try again.');
      setTimeout(() => setError(null), 4000);
    } finally {
      setLoadingSection(null);
    }
  };

  // ===========================================
  // Render
  // ===========================================
  return (
    <div className="idea-tab">
      {/* Header with title and save status */}
      <div className="idea-tab__header">
        <div className="idea-tab__title">
          <Lightbulb size={18} />
          <h3>Idea Workshop</h3>
          <span className="idea-tab__subtitle">
            The big rocks — cornerstone questions for your app
          </span>
        </div>
        
        {/* Save status indicator */}
        <div className="idea-tab__status">
          {saving && (
            <span className="idea-tab__saving">
              <Save size={14} />
              Saving...
            </span>
          )}
          {saved && !saving && (
            <span className="idea-tab__saved">
              <CheckCircle size={14} />
              Saved
            </span>
          )}
          {error && (
            <span className="idea-tab__error">{error}</span>
          )}
        </div>
      </div>

      {/* Workshop Sections Grid - 2x2 layout */}
      <div className="idea-tab__grid">
        {SECTIONS.map(section => {
          const Icon = section.icon;
          const charCount = formData[section.id].length;
          const maxChars = 5000;
          const isLoading = loadingSection === section.id;
          
          return (
            <div key={section.id} className="idea-section">
              {/* Section Header with icon, label, and README button */}
              <div className="idea-section__header">
                <div 
                  className="idea-section__icon"
                  style={{ 
                    background: `${section.color}15`,
                    color: section.color 
                  }}
                >
                  <Icon size={16} />
                </div>
                <h4>{section.label}</h4>
                
                {/* README.md Button - AI-powered content generation */}
                <button
                  className={`idea-section__readme-btn ${isLoading ? 'loading' : ''}`}
                  onClick={() => handleGenerateFromReadme(section.id)}
                  disabled={isLoading || !project.readmeContent}
                  title={project.readmeContent 
                    ? 'Generate from README.md using AI' 
                    : 'Upload a README.md on Overview tab first'}
                >
                  {isLoading ? (
                    <Loader size={12} className="spin" />
                  ) : (
                    <FileText size={12} />
                  )}
                  <span>README.md</span>
                </button>
              </div>

              {/* Editable Textarea */}
              <textarea
                className="idea-section__input"
                value={formData[section.id]}
                onChange={(e) => handleChange(section.id, e.target.value)}
                placeholder={section.placeholder}
                maxLength={maxChars}
              />

              {/* Footer with character count */}
              <div className="idea-section__footer">
                <span 
                  className="idea-section__count"
                  style={{ 
                    color: charCount > maxChars * 0.9 ? '#ef4444' : undefined 
                  }}
                >
                  {charCount.toLocaleString()} / {maxChars.toLocaleString()}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default IdeaTab;
